/**
 * Navigation de l'espace prestataire.
 *
 * `disponible: false` n'est pas décoratif : un écran qui n'existe pas encore est
 * ANNONCÉ comme tel (mention « bientôt ») au lieu d'être un lien qui ne mène
 * nulle part. Une entrée devient disponible le jour où sa page existe.
 */
export interface EntreeNavigation {
  readonly libelle: string;
  readonly icone: string;
  readonly chemin: string;
  readonly disponible: boolean;
  /**
   * Entrée réservée au rôle `admin` : elle n'est même pas affichée aux autres.
   * Le serveur refuse de toute façon (403) — cacher n'est pas protéger, c'est
   * éviter de proposer une porte fermée.
   */
  readonly adminSeulement?: boolean;
}

export interface SectionNavigation {
  readonly titre: string;
  readonly entrees: readonly EntreeNavigation[];
}

export const SECTIONS: readonly SectionNavigation[] = [
  {
    titre: 'Mon activité',
    entrees: [
      { libelle: 'Tableau de bord', icone: 'layout-dashboard', chemin: '/', disponible: true },
      {
        libelle: 'Liste des clients',
        icone: 'users',
        chemin: '/clients',
        disponible: true,
      },
      {
        libelle: 'Mes interventions',
        icone: 'calendar',
        chemin: '/interventions',
        disponible: true,
      },
      {
        libelle: 'Mes rapports',
        icone: 'file-text',
        chemin: '/rapports',
        disponible: true,
      },
      { libelle: 'Mes factures', icone: 'receipt', chemin: '/a-venir/factures', disponible: false },
    ],
  },
  {
    titre: 'Conformité',
    entrees: [
      {
        libelle: 'Mes documents',
        icone: 'shield-check',
        chemin: '/a-venir/documents',
        disponible: false,
      },
      {
        libelle: 'Mes certifications',
        icone: 'award',
        chemin: '/a-venir/certifications',
        disponible: false,
      },
    ],
  },
  {
    titre: 'Mon compte',
    entrees: [
      {
        libelle: 'Mot de passe',
        icone: 'shield-check',
        chemin: '/mon-compte',
        disponible: true,
      },
    ],
  },
  {
    titre: 'Support',
    entrees: [
      {
        libelle: 'Messagerie',
        icone: 'message-square',
        chemin: '/a-venir/messagerie',
        disponible: false,
      },
      {
        libelle: 'Notifications',
        icone: 'bell',
        chemin: '/a-venir/notifications',
        disponible: false,
      },
    ],
  },
  // L'administration ferme la marche : ce sont des gestes de maintenance, pas le
  // travail quotidien — ils se rangent en bas, sous les écrans qu'on ouvre tous
  // les jours. Un prestataire ne la voit pas du tout.
  {
    titre: 'Administration',
    entrees: [
      {
        libelle: 'Comptes',
        icone: 'users',
        chemin: '/administration',
        disponible: true,
        adminSeulement: true,
      },
      {
        libelle: 'Journal',
        icone: 'file-text',
        chemin: '/journal',
        disponible: true,
        adminSeulement: true,
      },
    ],
  },
];

/**
 * Les sections que ce compte a le droit de voir.
 *
 * Une section dont toutes les entrées sont réservées à l'administration disparaît
 * entièrement pour un prestataire — pas de titre orphelin dans la barre latérale.
 */
export function sectionsPour(
  role: 'prestataire' | 'admin' | undefined,
): readonly SectionNavigation[] {
  const admin = role === 'admin';
  return SECTIONS.map((section) => ({
    ...section,
    entrees: section.entrees.filter((entree) => admin || !entree.adminSeulement),
  })).filter((section) => section.entrees.length > 0);
}
